import { ReactNode, useRef } from "react";
import { Loader2 } from "lucide-react";
import SubscriptionGate from "@/components/subscription/SubscriptionGate";
import { useAccessTier } from "@/hooks/useAccessTier";

/**
 * The single customer access gate for HeirWise.
 *
 * Admin / parent / paid / billing-exempt / internal-QA identities render the
 * app. Everyone else falls through to the Stripe-backed SubscriptionGate,
 * which is the only path that can create customer entitlement. Browser state
 * (including a Checkout success URL) can never grant access here — entitlement
 * is read from server-authoritative household billing state.
 */
export default function AccessGate({ children }: { children: ReactNode }) {
  const { loading, isAdmin, hasPaidAccess } = useAccessTier();

  // Once resolved, background revalidation must never blank the app again.
  const resolvedOnceRef = useRef(false);
  if (!loading) resolvedOnceRef.current = true;

  if (loading && !resolvedOnceRef.current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }
  if (loading) return <>{children}</>;
  if (isAdmin || hasPaidAccess) return <>{children}</>;

  return <SubscriptionGate>{children}</SubscriptionGate>;
}
